// عميل HTTP — طبقة رقيقة فوق fetch مع أخطاء مفهومة وكشف حالة الاتصال.
// هذا الملف هو نقطة التماس الوحيدة بين الواجهة والخادم.

import { apiUrl, API_BASE } from './config.js';

export class ApiError extends Error {
  constructor(status, code, message, details){
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
  get isAuth(){ return this.status === 401; }
  get isOffline(){ return this.status === 0; }
}

const listeners = new Set();
let online = navigator.onLine;

export function onConnectivity(fn){
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function setOnline(value){
  if (online === value) return;
  online = value;
  listeners.forEach(fn => { try { fn(online); } catch (err){ console.warn(err); } });
}
export function isOnline(){ return online; }

window.addEventListener('online',  () => setOnline(true));
window.addEventListener('offline', () => setOnline(false));

async function request(method, path, body, { timeout = 20000 } = {}){
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  // GET/HEAD بجسم يرميه fetch استثناءً متزامناً، فيقع في مصيدة catch أدناه
  // ويُقرأ «انقطاع اتصال» — والتطبيق كلّه ينتقل إلى وضع بلا إنترنت بلا سبب.
  // null هنا يعني «بلا جسم» تماماً كـ undefined.
  const hasBody = body !== undefined && body !== null
                  && method !== 'GET' && method !== 'HEAD';

  let res;
  try {
    res = await fetch(apiUrl(path), {
      method,
      // include لا same-origin: الواجهة والـAPI على أصلين مختلفين، وبدون هذا
      // لا يرسل المتصفح كوكي الجلسة ولا يقبل Set-Cookie — فيبدو الدخول ناجحاً
      // ثم يجد المستخدم نفسه خارج الحساب فوراً. الكوكي تبقى httpOnly في الحالتين.
      credentials: 'include',
      headers: hasBody ? { 'Content-Type': 'application/json' } : {},
      body: hasBody ? JSON.stringify(body) : undefined,
      signal: controller.signal
    });
  } catch (err){
    clearTimeout(timer);
    // فشل الشبكة لا يعني بالضرورة أن المتصفح يعرف أنه غير متصل
    setOnline(false);
    throw new ApiError(0, 'offline',
      err.name === 'AbortError' ? 'انتهت مهلة الطلب' : 'لا يوجد اتصال بالخادم');
  } finally {
    clearTimeout(timer);
  }

  setOnline(true);

  if (res.status === 204) return null;

  let payload = null;
  const type = res.headers.get('content-type') || '';
  if (type.includes('application/json')){
    try { payload = await res.json(); } catch { payload = null; }
  }

  if (!res.ok){
    const e = payload && payload.error;
    throw new ApiError(
      res.status,
      (e && e.code) || 'error',
      (e && e.message) || `فشل الطلب (${res.status})`,
      e && e.details
    );
  }
  return payload;
}

export const api = {
  get:    (path, opts)       => request('GET', path, undefined, opts),
  post:   (path, body, opts) => request('POST', path, body ?? {}, opts),
  patch:  (path, body, opts) => request('PATCH', path, body ?? {}, opts),
  delete: (path, opts)       => request('DELETE', path, undefined, opts),

  // ----- المصادقة -----
  register: (username, password, baseCurrency = 'ILS') =>
              request('POST', '/api/auth/register', { username, password, baseCurrency }),
  login:    (username, password) => request('POST', '/api/auth/login', { username, password }),
  logout:   ()                   => request('POST', '/api/auth/logout', {}),
  me:       ()                   => request('GET',  '/api/auth/me'),
  recover:  (username, recoveryCode, newPassword) =>
              request('POST', '/api/auth/recover', { username, recoveryCode, newPassword }),
  changePassword: (currentPassword, newPassword) =>
              request('POST', '/api/auth/change-password', { currentPassword, newPassword }),
  /** حذف الحساب نهائياً — كلمة المرور يتحقّق منها الخادم، لا الواجهة. */
  deleteAccount: password => request('DELETE', '/api/auth/account', { password }),

  /** الجلسات الحيّة — جهاز لكل جلسة، والحالية مُعلَّمة بـ isCurrent. */
  listSessions:  () => request('GET', '/api/auth/sessions'),
  revokeOthers:  () => request('DELETE', '/api/auth/sessions'),

  // ----- المزامنة -----
  // entries=false: نسحب الفهرس وحده. تاريخ الحركات يُطلب لفترته عند العرض.
  pull: since => request('GET', '/api/sync?entries=false'
    + (since ? `&since=${encodeURIComponent(since)}` : '')),

  /** حركات فترة بعينها — تُعرض ثم تُنسى، لا تُخزَّن على الجهاز. */
  listEntries: (from, to, { untyped = false } = {}) => request('GET', '/api/entries'
    + `?from=${encodeURIComponent(new Date(from).toISOString())}`
    + `&to=${encodeURIComponent(new Date(to).toISOString())}`
    + (untyped ? '&untyped=true' : ''), undefined, { timeout: 30000 }),

  /** عدّادات الاستعمال — حدود الشهر من العميل لأن الخادم بتوقيت UTC. */
  getStats: (monthFrom, monthTo) => request('GET', '/api/entries/stats'
    + `?monthFrom=${encodeURIComponent(new Date(monthFrom).toISOString())}`
    + `&monthTo=${encodeURIComponent(new Date(monthTo).toISOString())}`),
  push: operations => request('POST', '/api/sync', { operations }, { timeout: 40000 }),

  // ----- العائلة -----
  household:     ()     => request('GET', '/api/household'),
  updateHousehold: patch => request('PATCH', '/api/household', patch),
  joinHousehold: inviteCode => request('POST', '/api/household/join', { inviteCode }),
  rotateInvite:  ()     => request('POST', '/api/household/invite/rotate', {}),

  // ----- البيانات -----
  exportData: () => request('GET', '/api/data/export'),
  importData: dump => request('POST', '/api/data/import', dump, { timeout: 60000 }),
  demoData:   days => request('POST', '/api/data/demo', { days }, { timeout: 60000 }),
  resetData:  ()   => request('POST', '/api/data/reset', { confirm: 'DELETE' }),

  /** ماذا سيُحذف قبل التأكيد — المدى [from, to) وحدوده من العميل كبقية المسارات. */
  purgePreview: (from, to) => request('GET', '/api/data/purge/preview'
    + `?from=${encodeURIComponent(new Date(from).toISOString())}`
    + `&to=${encodeURIComponent(new Date(to).toISOString())}`),

  purgeRange: (from, to) => request('POST', '/api/data/purge', {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    confirm: 'DELETE',
  }, { timeout: 30000 }),

  /** عنوان الخادم المستخدم فعلياً — يُعرض في الإعدادات للتشخيص. */
  baseUrl: () => API_BASE || location.origin
};
